import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Heading, IconProps, Text } from '@chakra-ui/react';

import { StatsSection } from '~/components/stats-section';
import { ArrowClockwiseIcon, DiscIcon, GearFineIcon, PlantIcon } from '~/features/enterprise/landing-pages/icons';

export type ImpactConfig = {
  stats: {
    id: string;
    iconId: string;
  }[];
};

function getStatIcon(iconId: string, customProps?: IconProps): ReactNode {
  const props = { color: '#7BA2FF', height: '24px', width: '24px', ...customProps };
  switch (iconId) {
    case 'gear':
      return <GearFineIcon {...props} />;
    case 'plant':
      return <PlantIcon {...props} />;
    case 'arrow-clockwise':
      return <ArrowClockwiseIcon {...props} />;
    default:
      return null;
  }
}

export function ImpactSection({ config, slug }: { config: ImpactConfig; slug: string }) {
  const { t } = useTranslation();
  const tPrefix = `enterprise.${slug}.impact`;

  return (
    <StatsSection
      contentGap={{ base: '32px', md: '42px' }}
      footnote={t(`${tPrefix}.footnote`)}
      headerIcon={<DiscIcon color='blue.300' height='18px' width='18px' />}
      headerSubtitle={t(`${tPrefix}.subtitle`)}
      headerDescription={
        <Text color='grey.100' margin='0 auto' maxWidth='843px'>
          {t(`${tPrefix}.description`)}
        </Text>
      }
      headerTitle={
        <Heading
          as='h2'
          color='white'
          fontSize={{ base: '2.25rem', md: '3.25rem' }}
          fontWeight='600'
          letterSpacing='-2px'
          lineHeight='1.2'
        >
          {t(`${tPrefix}.title`)}
        </Heading>
      }
    >
      <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: '16px', md: '23px' }} justifyContent='center'>
        {config.stats.map((stat) => (
          <Flex
            key={stat.id}
            bg='rgba(255, 255, 255, 0.08)'
            borderRadius='8px'
            direction='column'
            flex={{ base: 'unset', md: '1' }}
            gap='13px'
            maxWidth={{ md: '246px' }}
            padding='24px'
          >
            <Flex
              alignItems='center'
              bg='rgba(255, 255, 255, 0.08)'
              borderRadius='8px'
              height='48px'
              justifyContent='center'
              width='48px'
            >
              {getStatIcon(stat.iconId, {
                height: '36px',
                width: '36px',
              })}
            </Flex>
            <Text color='#7BA2FF' fontFamily='heading' fontSize='2.875rem' fontWeight='600' lineHeight='1'>
              {t(`${tPrefix}.stats.${stat.id}.value`)}
            </Text>
            <Text color='white' fontSize='0.875rem' fontWeight='500' lineHeight='1.4'>
              {t(`${tPrefix}.stats.${stat.id}.description`)}
            </Text>
          </Flex>
        ))}
      </Flex>
    </StatsSection>
  );
}
